// First payment plan at build submission (Oct 21, 2026).
//
// Greg: "i need to move the gate for initial payment from roberts 'schedule
// build' to submitting the build ... if inital payment has not been captured,
// i need them to verify check by build day, online payment by build day or
// check with mortgagor for endorsement or other where they can type in the
// reason. also a note that says commissions will not go out until all initial
// payment is cleared"
//
// Every way a deal crosses into the build stages asks the question: Submit for
// build approval (RMs, pipeline cards), the deal page's Advance (Chad, 54 of
// the last 68 submissions), Release to Robert (since Oct 22, 2026 it submits
// the build to the Build Approvals board instead of jumping to schedule_build),
// the pipeline stage dropdown, Roof Master's status and the admin override.
// The database refuses the crossing without a plan once
// finance_settings.build_payment_plan_gate is on
// (migration 20261021c_build_payment_plans.sql), and Robert's Schedule build
// reads the plan from build_payment_plans instead of asking again.

import { supabase } from "./supabase";
import { calcScope } from "./scopeCalc";
import type { Deal } from "./types";
import type { PaymentPlanKey } from "../../supabase/functions/build-calendar/eventText";

export {
  PAYMENT_PLAN_KEYS, PAYMENT_PLAN_LABELS, isPaymentPlanKey, planNeedsNote, planPhrase, type PaymentPlanKey,
} from "../../supabase/functions/build-calendar/eventText";

/** Under each choice in the submit form. */
export const PAYMENT_PLAN_HINTS: Partial<Record<PaymentPlanKey, string>> = {
  check_by_build_day: "You've confirmed with the homeowner that the check will be ready by build day.",
  online: "The homeowner pays in their portal by build day. The build text carries the pay link.",
  mortgage_endorsement: "The insurance check is with the mortgage company for endorsement.",
  other: "Type the reason below.",
};

export const COMMISSION_NOTE = "Commissions will not go out until all of the first payment has cleared.";

/** The stages a submission lands in (SQL build_plan_gated_target). */
export const GATED_TARGETS = new Set(["build_submitted", "schedule_build", "build_scheduled"]);

/** Already submitted, or past the build: moving from one of these is not a new
 *  submission. ⚠ Mirror of SQL build_plan_submitted_stage (migration 20261021c). */
export const SUBMITTED_STAGES = new Set([
  "build_submitted", "schedule_build", "build_scheduled", "roof_complete",
  "ready_for_supps", "ready_for_updated_invoice", "supps_waiting_on_claims", "supps_waiting_on_final_invoice",
  "collecting", "delinquent", "register_oc_warranty", "closed_won",
  "trades_in_progress", "trades_complete", "collect_payment",
  "commission_ready", "commission_approved", "commission_paid",
]);

/** True when moving a deal from `from` to `to` submits its build. */
export function crossesBuildSubmission(from: string | null | undefined, to: string | null | undefined): boolean {
  return !!to && GATED_TARGETS.has(to) && !SUBMITTED_STAGES.has(from ?? "");
}

export interface FirstPaymentStatus {
  label: "first payment" | "50% deposit";
  /** The whole first payment (calcScope.firstPaymentToBardin). null = no scope entered. */
  due: number | null;
  /** Cleared money on the deal, both slots. */
  received: number;
  /** Recorded but still clearing (a check not deposited, an ACH settling). */
  inFlight: number;
  /** Still owed after everything recorded. null = unknown. */
  owed: number | null;
  /** received + in flight covers it, to within 50 cents (SQL build_first_payment_captured). */
  isCaptured: boolean;
  /** Cleared money alone covers it. */
  isCleared: boolean;
}

const n = (v: unknown) => Number(v ?? 0) || 0;

/** The first payment on a full deal row: the number the homeowner's portal shows.
 *  A trade billed to the homeowner is never in it (Oct 29, 2026: due at
 *  completion; calcScope's firstPaymentToBardin and SQL fn_deal_first_payment
 *  both leave deals.trade_ho_total out), so the hold below and the build
 *  payment-plan gate do not move when a trade is added. */
export function firstPaymentStatus(deal: Record<string, unknown>): FirstPaymentStatus {
  const isRetail = deal.is_retail === true;
  const label = isRetail ? "50% deposit" : "first payment";
  const received = n(deal.pre_build_amount_received) + n(deal.post_build_amount_received);
  const inFlight = n(deal.pre_build_amount_in_flight) + n(deal.post_build_amount_in_flight);
  const scopeKnown = isRetail ? n(deal.retail_contract_total) > 0 : n(deal.rcv_total) > 0;
  if (!scopeKnown) {
    return { label, due: null, received, inFlight, owed: null, isCaptured: false, isCleared: false };
  }
  const due = Math.round(calcScope(deal as unknown as Deal).firstPaymentToBardin * 100) / 100;
  const owed = Math.max(0, Math.round((due - received - inFlight) * 100) / 100);
  return {
    label, due, received, inFlight, owed,
    isCaptured: received + inFlight >= due - 0.5,
    isCleared: received >= due - 0.5,
  };
}

/** Reached only with the balance settled (SQL fn_deal_first_payment_in). */
const CLOSED_OUT_STAGES = new Set(["register_oc_warranty", "closed_won"]);

export interface CommissionHold {
  label: FirstPaymentStatus["label"];
  due: number;
  received: number;
  inFlight: number;
  /** Non-collect revenue (up to the first payment) + the finance write-off:
   *  money Bardin won't collect, so it can't hold the commission. */
  credited: number;
  /** What still has to clear. */
  short: number;
  message: string;
}

/** Oct 22, 2026 (Greg: "Hold at approval") — COMMISSION_NOTE is enforced: a
 *  deal's RM / TL / ASM commission can't be approved until all of its first
 *  payment has cleared, to within 50 cents. Money still clearing doesn't
 *  count. Non-null while the commission waits. A job closed out, or one with
 *  no scope to compute the first payment from, is not held. Nor is one Chad
 *  has overridden (Oct 28, 2026: commission_first_payment_override_at, set by
 *  the audited commission_override_first_payment_hold RPC from the board).
 *  ⚠ Mirror of SQL commission_first_payment_hold (migrations 20261022 and
 *  20261028), which refuses the approval in the database. Change both together. */
export function commissionFirstPaymentHold(deal: Record<string, unknown>): CommissionHold | null {
  if (deal.commission_first_payment_override_at) return null;
  if (CLOSED_OUT_STAGES.has(String(deal.stage ?? ""))) return null;
  const s = firstPaymentStatus(deal);
  if (s.due === null || s.due <= 0) return null;
  const nonCollect = deal.is_retail === true ? 0 : Math.min(Math.max(0, n(deal.non_commissionable)), s.due);
  const writtenOff = Math.max(0, n(deal.balance_written_off));
  if (s.received + nonCollect + writtenOff >= s.due - 0.5) return null;
  const short = Math.round((s.due - s.received - nonCollect - writtenOff) * 100) / 100;
  const counted = [`cleared ${usd(s.received)}`];
  if (nonCollect > 0) counted.push(`non-collect ${usd(nonCollect)}`);
  if (writtenOff > 0) counted.push(`written off ${usd(writtenOff)}`);
  const clearing = s.inFlight > 0 ? `; ${usd(s.inFlight)} recorded and still clearing` : "";
  const label = s.label.charAt(0).toUpperCase() + s.label.slice(1);
  return {
    label: s.label, due: s.due, received: s.received, inFlight: s.inFlight,
    credited: nonCollect + writtenOff, short,
    message: `${label} hasn't cleared: ${usd(short)} of ${usd(s.due)} is still out (${counted.join(" + ")}${clearing}). Commissions go out once all of it has cleared.`,
  };
}

export interface BuildPaymentPlan {
  id: string;
  deal_id: string;
  plan: PaymentPlanKey;
  note: string | null;
  first_payment_label: string;
  first_payment_due: number | null;
  stage_from: string;
  stage_to: string;
  source: string;
  actor_id: string | null;
  created_at: string;
}

function normalize(row: Record<string, unknown>): BuildPaymentPlan {
  const r = row as unknown as BuildPaymentPlan;
  return { ...r, first_payment_due: r.first_payment_due == null ? null : Number(r.first_payment_due) };
}

/** The plan that counts for each deal: the newest recorded since it last
 *  entered install_meeting. Deals without one are absent from the map. */
export async function fetchCurrentPlans(dealIds: string[]): Promise<Record<string, BuildPaymentPlan>> {
  const ids = [...new Set(dealIds.filter(Boolean))];
  if (ids.length === 0) return {};
  const { data, error } = await supabase.rpc("build_payment_plans_current", { p_deal_ids: ids });
  if (error) throw error;
  const out: Record<string, BuildPaymentPlan> = {};
  for (const row of (data ?? []) as Record<string, unknown>[]) {
    const p = normalize(row);
    out[p.deal_id] = p;
  }
  return out;
}

export async function fetchCurrentPlan(dealId: string): Promise<BuildPaymentPlan | null> {
  return (await fetchCurrentPlans([dealId]))[dealId] ?? null;
}

export type PlanSource = "submit_build" | "release_to_robert" | "pipeline" | "roof_master" | "admin_override";

export interface PlanChoice {
  plan: PaymentPlanKey | "";
  note: string;
}

/** A choice that can be recorded: a plan, and the reason when it's Other. */
export function choiceIsComplete(c: PlanChoice): boolean {
  if (!c.plan) return false;
  return c.plan !== "other" || c.note.trim().length >= 3;
}

/** Same plan and note as the one on file (nothing new to record). */
export function sameAsOnFile(c: PlanChoice, onFile: BuildPaymentPlan | null): boolean {
  if (!onFile || !c.plan) return false;
  return onFile.plan === c.plan && (onFile.note ?? "").trim() === c.note.trim();
}

/** Record the plan. The database checks that the actor is the signed-in person. */
export async function recordBuildPaymentPlan(input: {
  dealId: string;
  choice: PlanChoice;
  status: FirstPaymentStatus | null;
  stageFrom: string | null | undefined;
  stageTo: string;
  source: PlanSource;
  actorId: string | null | undefined;
}): Promise<BuildPaymentPlan> {
  if (!input.choice.plan) throw new Error("Pick how the first payment will be collected.");
  if (!input.actorId) throw new Error("Your account isn't on the team roster, so the plan can't be recorded.");
  const note = input.choice.note.replace(/\s+/g, " ").trim().slice(0, 500) || null;
  const { data, error } = await supabase.from("build_payment_plans").insert({
    deal_id: input.dealId,
    plan: input.choice.plan,
    note,
    first_payment_label: input.status?.label ?? "first payment",
    first_payment_due: input.status?.owed ?? null,
    stage_from: input.stageFrom ?? "",
    stage_to: input.stageTo,
    source: input.source,
    actor_id: input.actorId,
  }).select("*").single();
  if (error) throw error;
  return normalize(data as Record<string, unknown>);
}

/** The database refused a submission because no plan is on file. */
export function isPlanGateError(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const e = err as { hint?: unknown; message?: unknown };
  return e.hint === "build_payment_plan_required"
    || (typeof e.message === "string" && e.message.includes("will be collected before submitting the build"));
}

/** "$8,423.17" */
export function usd(v: number): string {
  return "$" + v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
